import { useEffect, useState } from "react";
import { CornerUpLeft, Pin, Plus, Smile } from "lucide-react";
import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr";
import { useQueryClient } from "@tanstack/react-query";
import Skeleton from "react-loading-skeleton";
import { KeyValuePairType } from "../../../types/enums";
import { TimelineActivityType } from "../../../types/enums/TimelineActivityType";
import { EntityStatus } from "@repo/types/enums";
import { UpdateTimelineActivityType } from "../../../types/enums/UpdateTimelineActivityType";
import { ActivityActionType } from "../../../types/enums/ui/ActivityActionType";
import { timelineActivityKeys } from "@repo/types/queries";
import type { TimelineActivityDTO } from "../../../types/dtos/TimelineActivityDTO";
import type { KeyValuePairDTO } from "../../../types/dtos/KeyValuePairDTO";
import type { TimelineActivityTypeItem } from "../../../types/ui/AddEditTimelineActivityProps";
import { KeyValueViewType } from "@repo/types/ui/keyValuePairs";
import type { RecordId } from "@repo/types/ui/tables";
import { useGetTicketById } from "../../../hooks/queries/useTickets";
import useGetTicketEmailMessages from "../../../hooks/queries/useEmailMessages";
import { useApiAccess } from "@repo/hooks/contexts";
import useGetTimelineActivitiesByTicketId, {
  useUpdateTimelineActivity,
} from "../../../hooks/queries/useTimelineActivity";
import { useGetKeyValuePairs } from "../../../hooks/queries/useKeyValuePairs";
import { useDialog } from "../../../hooks/contexts/useDialog";
import EmailReplyContainer from "../../emailMessages/EmailReplyContainer";
import AddUpdateTimelineActivityContainer from "./AddEditTimelineActivityContainer";
import { TimelineActivityContent } from "./TimelineActivityContent";
import { AddEditActivity } from "./AddEditActivity";
import Button from "../../ui/button/Button";
import KeyValueMenu from "../../ui/menu/KeyValueMenu";
import { KeyValuePairIcon } from "../../ui/menu/KeyValuePairIcon";
import formatUtcDateTime from "../../../helpers/dateTimeHelper";

export interface TicketTimeLineProps {
  ticketId: RecordId;
}

export default function TicketTimeLine({ ticketId }: TicketTimeLineProps) {
  const [addActivityType, setAddActivityType] = useState<
    TimelineActivityTypeItem | undefined
  >(undefined);
  const [addUpdateActivity, setAddUpdateActivity] = useState<
    TimelineActivityDTO | undefined
  >(undefined);
  const [isAddCustomActivityOpen, setIsAddCustomActivityOpen] = useState(false);
  const { data: ticketEmailMessages } = useGetTicketEmailMessages(ticketId);
  const { data: timelineActivities, isPending: timelineActivitiesLoading } =
    useGetTimelineActivitiesByTicketId(ticketId);
  const { data: ticket, isPending: ticketLoading } = useGetTicketById(ticketId);
  const { data: timelineActivityTypeKeyValuePairs } = useGetKeyValuePairs(
    KeyValuePairType.TimelineActivityType,
    (kv) =>
      kv.map(
        (keyValuePair: KeyValuePairDTO): TimelineActivityTypeItem => ({
          viewType: KeyValueViewType.iconAndText,
          Id: keyValuePair.Id,
          Value: keyValuePair.Value,
          icon: <KeyValuePairIcon keyValuePair={keyValuePair} />,
          enumValue: Number(keyValuePair.PairTypeEnumValue),
          classNames: "p-1.5 hover:bg-gray-100",
          style: {
            fontWeight: "normal",
            color: "#000000",
            fontSize: "13px",
          },
        }),
      ),
  );

  const { mutateAsync: updateTimelineActivity } = useUpdateTimelineActivity();

  const dialog = useDialog();

  const handleAddActivityTypeChange = async (
    keyValuePairId: number | undefined,
    entityId?: number,
  ) => {
    const selectedTimelineActivityTypeItem =
      timelineActivityTypeKeyValuePairs?.find(
        (type) => type.Id === keyValuePairId,
      );
    if (!selectedTimelineActivityTypeItem) return;

    setAddActivityType(selectedTimelineActivityTypeItem);

    const newTimelineActivityDTO: TimelineActivityDTO = {
      Id: null,
      ActivityTypeKeyValuePairId: selectedTimelineActivityTypeItem.Id,
      ActivityTypeKeyValuePairEnumValue:
        selectedTimelineActivityTypeItem.enumValue,
      ActivityTypeKeyValuePairValue: selectedTimelineActivityTypeItem.Value,
      Content: "",
      EntityId: entityId ?? 0,
      TicketGuid: ticketId,
      CreatedOn: "",
      CreatedByUser: "",
      ScheduledDate: "",
      ScheduledStartTime: "",
      ScheduledEndTime: "",
      PinnedRank: null,
      Status: EntityStatus.Active,
      MentionUserIds: [],
    };

    setAddUpdateActivity(newTimelineActivityDTO);
  };

  const handleEditActivityActionTypeClick = async (
    activityActionType: number,
    activityId: RecordId,
  ) => {
    if (!timelineActivities) return;

    const selectedTimelineActivity = timelineActivities.find(
      (ta) => ta.Id === activityId,
    );

    if (!selectedTimelineActivity) return;

    switch (activityActionType) {
      case ActivityActionType.PinToTop: {
        const res = await updateTimelineActivity({
          Id: activityId,
          TicketId: ticketId,
          IsPinnedToTop: true,
          UpdateType: UpdateTimelineActivityType.PinToTop,
        });

        if (res.Result) {
          timelineActivityKeys.invalidateListByRelationId(
            queryClient,
            ticketId,
          );
        }

        break;
      }

      case ActivityActionType.Unpin: {
        const res = await updateTimelineActivity({
          Id: activityId,
          TicketId: ticketId,
          IsPinnedToTop: false,
          UpdateType: UpdateTimelineActivityType.Unpin,
        });

        if (res.Result) {
          timelineActivityKeys.invalidateListByRelationId(
            queryClient,
            ticketId,
          );
        }

        break;
      }

      case ActivityActionType.ReplyAll:
      case ActivityActionType.Edit: {
        setAddUpdateActivity(selectedTimelineActivity);

        setAddActivityType(
          timelineActivityTypeKeyValuePairs?.find(
            (type) =>
              type.Id === selectedTimelineActivity.ActivityTypeKeyValuePairId,
          ),
        );

        break;
      }

      case ActivityActionType.Delete: {
        const confirmed = await dialog.confirm({
          type: "confirm",
          title: "Delete Timeline Activity",
          message: "Are you sure you want to delete this timeline activity?",
        });

        if (!confirmed) return;

        const res = await updateTimelineActivity({
          Id: activityId,
          TicketId: ticketId,
          Status: EntityStatus.Deleted,
          UpdateType: UpdateTimelineActivityType.Status,
        });

        if (res.Result) {
          timelineActivityKeys.invalidateListByRelationId(
            queryClient,
            ticketId,
          );
        }
        break;
      }

      default:
        break;
    }
  };

  const { accessToken } = useApiAccess();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!accessToken) return;

    const connection = new HubConnectionBuilder()
      .withUrl(`${import.meta.env.VITE_API_URL}/hub/timelineActivities`, {
        accessTokenFactory: () => accessToken,
        withCredentials: true,
      })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Information)
      .build();

    connection
      .start()
      .then(() => console.log(`✅ ticket hub connected`))
      .catch((err) => console.error(`❌ ticket hub connection error:`, err));

    connection.on("NotifyNewTimelineActivity", (ticketGuid: string) => {
      console.log("New timeline activity received via SignalR:", ticketGuid);

      timelineActivityKeys.invalidateListByRelationId(queryClient, ticketGuid);
    });
  }, [accessToken, queryClient]);

  const loading = timelineActivitiesLoading || ticketLoading;

  if (!ticket) {
    return null;
  }

  function ActivityEditorContainer() {
    if (!addUpdateActivity || !ticket) {
      return null;
    }

    if (
      addUpdateActivity.ActivityTypeKeyValuePairEnumValue ===
        TimelineActivityType.Email &&
      ticketEmailMessages &&
      ticketEmailMessages.length > 0
    ) {
      const emailMessage = ticketEmailMessages.find(
        (em) => em.Id === addUpdateActivity.EntityId,
      );

      if (!emailMessage) return null;

      return (
        <EmailReplyContainer
          ticket={ticket}
          replyToEmailMessage={emailMessage}
          onClose={() => {
            setAddUpdateActivity(undefined);
          }}
        />
      );
    } else {
      return (
        <AddUpdateTimelineActivityContainer
          ticket={ticket}
          activity={addUpdateActivity}
          activityType={addActivityType}
          onClose={() => setAddUpdateActivity(undefined)}
        />
      );
    }
  }

  return (
    <div className="relative">
      <div className="px-6 py-4 mb-4 border-b border-b-gray-200 w-full">
        <strong className="text-lg font-medium">Timeline</strong>
      </div>

      <div className="flex">
        <div className="px-3 py-0 mb-4">
          {loading ? (
            <Skeleton width={100} height={30} />
          ) : (
            <Button
              className="text-sm max-h-9"
              variant="primary"
              onClick={() =>
                handleAddActivityTypeChange(
                  timelineActivityTypeKeyValuePairs?.find(
                    (type) => type.enumValue === TimelineActivityType.Email,
                  )?.Id,
                  ticketEmailMessages
                    ? ticketEmailMessages[ticketEmailMessages.length - 1].Id
                    : 0,
                )
              }
            >
              <CornerUpLeft size={15} />
              Reply
            </Button>
          )}
        </div>

        <div className="px-3 py-0 mb-4">
          <KeyValueMenu
            items={timelineActivityTypeKeyValuePairs?.filter(
              (type) => type.enumValue !== TimelineActivityType.Email,
            )}
            onValueChange={async (key: number) =>
              handleAddActivityTypeChange(key)
            }
            selectedKey={addActivityType?.Id}
            triggerRenderer={(_, open) => (
              <div
                className={`flex space-x-1 p-2 hover:bg-blue-300 ${
                  open ? "bg-blue-200 rounded" : ""
                }`}
              >
                <Plus size={15} /> <div>Add activity</div>
              </div>
            )}
            handleTriggerClick={() => setIsAddCustomActivityOpen(false)}
            customActionTriggerRenderer={(onClick) => (
              <div
                onClick={() => {
                  setIsAddCustomActivityOpen(true);
                  onClick();
                }}
                className="flex w-full space-x-1 text-sm p-2 items-center cursor-pointer hover:bg-gray-100 rounded"
              >
                <Plus size={15} /> <div>Add custom activity</div>
              </div>
            )}
            customActionTemplateRenderer={() => (
              <AddEditActivity
                onClose={() => {
                  setIsAddCustomActivityOpen(false);
                  setAddActivityType(undefined);
                }}
              />
            )}
            popupClassNames={isAddCustomActivityOpen ? "w-60" : "w-50"}
          />
        </div>
      </div>

      <div className="px-15 py-15 bg-gray-100 overflow-scroll max-h-[600px]">
        {loading ? (
          <Skeleton count={5} />
        ) : timelineActivities && timelineActivities.length > 0 ? (
          timelineActivities.map((timelineActivity) => (
            <>
              <div className="flex justify-between space-x-1 text-sm mb-2 text-gray-500">
                {timelineActivityTypeKeyValuePairs &&
                  (() => {
                    const kvItem = timelineActivityTypeKeyValuePairs.find(
                      (kv) =>
                        kv.Id === timelineActivity.ActivityTypeKeyValuePairId,
                    );
                    return kvItem ? (
                      <>
                        {
                          <div className="flex items-center space-x-1">
                            <div className="ml-[-35px]">{kvItem.icon}</div>

                            <div className="ml-2">{kvItem.Value}</div>
                          </div>
                        }
                      </>
                    ) : null;
                  })()}

                <div className="flex space-x-2 items-center">
                  {formatUtcDateTime(timelineActivity.CreatedOn)}

                  {timelineActivity.PinnedRank && (
                    <div className="flex items-center space-x-1 p-1 ml-2 bg-blue-200 rounded">
                      <Pin size={14} className="rotate-30" /> <div>Pinned</div>
                    </div>
                  )}
                </div>
              </div>

              <div
                key={timelineActivity.Id}
                className="px-6 py-4 space-y-3 border border-gray-200 bg-white mb-6 rounded-md"
              >
                <div className="items-center space-x-2 text-gray-500">
                  <TimelineActivityContent
                    ticketGuid={ticket.TicketGuid}
                    timelineActivity={timelineActivity}
                    handleActivityActionTypeClick={
                      handleEditActivityActionTypeClick
                    }
                  />
                </div>
                <div className="flex-grow h-px bg-gray-300 mr-[-22px] ml-[-22px]"></div>
                <Smile className="inline mb-1" size={16} />{" "}
              </div>
            </>
          ))
        ) : (
          <p className="text-gray-600">No records.</p>
        )}
      </div>

      {addUpdateActivity && <ActivityEditorContainer />}
    </div>
  );
}
